import { Link } from "react-router-dom";
import {
  Users,
  Calculator,
  Smartphone,
  ShieldCheck,
  Clock,
  CalendarDays,
  FileText,
  Shield,
  BarChart3,
  UserPlus,
  Briefcase,
  GitBranch,
  Zap,
  Target,
  Scale,
  Sparkles,
  TrendingUp,
  Network,
  Landmark,
  HeartPulse,
  Receipt,
  IndianRupee,
  Handshake,
  ArrowRight,
  Building2,
  MapPin,
  Mail,
  Phone,
  CheckCircle2,
  Layers,
  Eye,
  Workflow,
  LineChart,
  Expand,
  UserCircle,
} from "lucide-react";
import MarketingLayout from "../marketing/MarketingLayout";
import SiteMeta from "../marketing/SiteMeta";
import ScrollReveal from "../marketing/ScrollReveal";
import FeatureCard from "../marketing/FeatureCard";
import ApproachCard from "../marketing/ApproachCard";
import ComplianceCard from "../marketing/ComplianceCard";
import CTASection from "../marketing/CTASection";
import ContactCard from "../marketing/ContactCard";
import { COMPANY, MARKETING_ROUTES } from "../marketing/routes";
import "./aboutUs.css";

const VALUE_STRIP = [
  { icon: Users, title: "HR Management", desc: "Manage your workforce from one platform." },
  { icon: Calculator, title: "Payroll Automation", desc: "Simplify salary processing and calculations." },
  { icon: UserCircle, title: "Employee Self-Service", desc: "Give employees easy access to their HR information." },
  { icon: ShieldCheck, title: "Compliance Focused", desc: "Support payroll and statutory processes for Indian businesses." },
];

const FEATURES = [
  { icon: Calculator, title: "Payroll Processing", description: "Salary calculations and payroll processing." },
  { icon: Users, title: "Employee Management", description: "Centralized employee records and information." },
  { icon: Clock, title: "Attendance & Shifts", description: "Manage attendance, shifts, schedules, and workforce time." },
  { icon: CalendarDays, title: "Leave & Holidays", description: "Manage leave policies, balances, holidays, and approvals." },
  { icon: Smartphone, title: "Employee Self-Service", description: "Give employees access to their HR information." },
  { icon: FileText, title: "Payslips & Documents", description: "Manage payslips and payroll-related documents." },
  { icon: Shield, title: "Statutory Compliance", description: "Support payroll deductions and statutory processes." },
  { icon: BarChart3, title: "Reports & Analytics", description: "Get better visibility through reports and analytics." },
  { icon: UserPlus, title: "Employee Onboarding", description: "Simplify employee onboarding workflows." },
  { icon: Briefcase, title: "Recruitment & Assets", description: "Manage recruitment and employee assets." },
  { icon: GitBranch, title: "HR Workflows", description: "Automate approvals and HR processes." },
];

const APPROACH = [
  { icon: Zap, title: "Automation", description: "We automate repetitive HR and payroll tasks so teams can reduce manual effort and process payroll more efficiently." },
  { icon: Target, title: "Accuracy", description: "Payroll requires accurate employee, salary, attendance, leave, and deduction information. Brixigo is designed to reduce manual calculations and help teams maintain reliable payroll records." },
  { icon: Scale, title: "Compliance", description: "We focus on helping organizations manage payroll and statutory processes in accordance with applicable Indian requirements." },
  { icon: Sparkles, title: "Simplicity", description: "HR software should not make everyday work more complicated. Brixigo is designed to provide a straightforward experience for HR teams, administrators, managers, and employees." },
  { icon: TrendingUp, title: "Scalability", description: "Whether an organization is starting out or managing a growing workforce, Brixigo is designed to support evolving HR and payroll requirements." },
  { icon: Network, title: "Connected Workforce", description: "Bring HR teams, managers, finance teams, and employees together through one connected platform." },
];

const COMPLIANCE = [
  { abbr: "PF", title: "Provident Fund", icon: Landmark },
  { abbr: "ESI", title: "Employee State Insurance", icon: HeartPulse },
  { abbr: "TDS", title: "Tax Deducted at Source", icon: Receipt },
  { abbr: "Professional Tax", title: "Professional Tax Management", icon: IndianRupee },
  { abbr: "Labour Welfare Fund", title: "LWF Support", icon: Handshake },
];

const BENEFITS = [
  { icon: Layers, label: "One connected platform" },
  { icon: Zap, label: "Reduced manual work" },
  { icon: Eye, label: "Better payroll visibility" },
  { icon: UserCircle, label: "Employee self-service" },
  { icon: Workflow, label: "Automated workflows" },
  { icon: ShieldCheck, label: "Compliance-focused payroll" },
  { icon: LineChart, label: "Better reporting" },
  { icon: Expand, label: "Scalable workforce management" },
];

const ESS_BULLETS = [
  "Employee self-service",
  "Payslip access",
  "Attendance visibility",
  "Leave management",
  "Manager access",
  "Mobile accessibility",
];

const COMMITMENTS = [
  { icon: ShieldCheck, title: "Reliable Technology", desc: "Build dependable tools for everyday HR operations." },
  { icon: TrendingUp, title: "Continuous Improvement", desc: "Continuously improve products based on business needs." },
  { icon: Phone, title: "Responsive Support", desc: "Help customers get the most value from Brixigo." },
];

function HeroVisual() {
  return (
    <div className="au-hero-visual" aria-hidden="true">
      <div className="au-dash au-float">
        <div className="au-dash-head">
          <span className="au-dash-dots"><i /><i /><i /></span>
          <span>HR & Payroll Overview</span>
        </div>
        <div className="au-dash-stats">
          <div className="au-stat">
            <span>Workforce</span>
            <strong>Employees</strong>
            <em>Active directory</em>
          </div>
          <div className="au-stat">
            <span>Payroll</span>
            <strong>Processing</strong>
            <em>Ready this cycle</em>
          </div>
          <div className="au-stat">
            <span>Attendance</span>
            <strong>On time</strong>
            <em>Shift coverage</em>
          </div>
        </div>
        <div className="au-dash-chart">
          {[42, 68, 54, 80, 61, 74, 88].map((h, i) => (
            <span key={i} style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="au-dash-rows">
          <div><b>Payslip</b><span>Generated</span></div>
          <div><b>Leave</b><span>Pending review</span></div>
          <div><b>Compliance</b><span>PF · ESI · TDS</span></div>
        </div>
      </div>
      <div className="au-float-card au-float-emp au-float-d1">
        <span className="au-avatar">AR</span>
        <div>
          <strong>Employee card</strong>
          <small>Profile · Department · Role</small>
        </div>
      </div>
      <div className="au-float-card au-float-pay au-float-d2">
        <FileText size={16} />
        <div>
          <strong>Payslip</strong>
          <small>Ready to share</small>
        </div>
      </div>
      <div className="au-float-card au-float-att au-float-d3">
        <span className="au-att-dot" />
        <div>
          <strong>Attendance</strong>
          <small>Present · On shift</small>
        </div>
      </div>
    </div>
  );
}

function WhyDashboard() {
  const tiles = [
    { label: "Employees", value: "Directory", icon: Users },
    { label: "Payroll", value: "In review", icon: Calculator },
    { label: "Attendance", value: "Live", icon: Clock },
    { label: "Leave", value: "Balances", icon: CalendarDays },
    { label: "Compliance", value: "PF · ESI · TDS", icon: Shield },
    { label: "Reports", value: "Insights", icon: BarChart3 },
  ];
  return (
    <div className="au-why-visual" aria-hidden="true">
      <div className="au-why-board au-float">
        <div className="au-why-board-head">Workforce dashboard</div>
        <div className="au-why-grid">
          {tiles.map(({ label, value, icon: Icon }) => (
            <div key={label} className="au-why-tile">
              <Icon size={16} />
              <strong>{label}</strong>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="au-float-card au-why-float-a au-float-d1">Payroll run in progress</div>
      <div className="au-float-card au-why-float-b au-float-d2">Leave request approved</div>
    </div>
  );
}

function PhoneMockup() {
  return (
    <div className="au-phone-wrap" aria-hidden="true">
      <div className="au-phone au-float">
        <div className="au-phone-notch" />
        <div className="au-phone-screen">
          <div className="au-phone-profile">
            <span className="au-avatar au-avatar-lg">KS</span>
            <div>
              <strong>My workspace</strong>
              <small>Employee self-service</small>
            </div>
          </div>
          <div className="au-phone-card">
            <span>Payslip</span>
            <strong>March 2026</strong>
            <em>Ready to view</em>
          </div>
          <div className="au-phone-row">
            <div>
              <small>Attendance</small>
              <b>Present</b>
            </div>
            <div>
              <small>Leave balance</small>
              <b>12 days</b>
            </div>
          </div>
          <div className="au-phone-action">Leave request</div>
        </div>
      </div>
    </div>
  );
}

export default function AboutUsPage() {
  return (
    <MarketingLayout className="about-us-page">
      <SiteMeta
        title="About Brixigo | HRMS & Payroll Software for Indian Businesses"
        description="Learn about Brixigo Technologies Private Limited and our mission to simplify HR management, payroll processing, employee self-service, and compliance for businesses across India."
      />

      <section className="au-hero" aria-labelledby="about-hero-heading">
        <div className="au-hero-inner">
          <div className="au-hero-copy">
            <p className="au-eyebrow">About Brixigo</p>
            <h1 id="about-hero-heading">Simplifying HR and Payroll for Modern Businesses</h1>
            <p className="au-hero-lead">
              Brixigo Technologies Private Limited is a technology company focused on simplifying human resource management and payroll for businesses and organizations across India.
            </p>
            <p className="au-hero-sub">
              Our HRMS and payroll platform is designed to help organizations manage their workforce, automate payroll processes, improve employee engagement, and stay aligned with applicable statutory requirements — all from one platform.
            </p>
            <div className="au-hero-actions">
              <Link to={MARKETING_ROUTES.features} className="mk-btn mk-btn-primary">
                Explore Brixigo <ArrowRight size={16} />
              </Link>
              <Link to={MARKETING_ROUTES.contact} className="mk-btn mk-btn-outline">
                Contact Us
              </Link>
            </div>
          </div>
          <HeroVisual />
        </div>
      </section>

      <section className="au-strip" aria-label="Platform values">
        <div className="au-strip-inner">
          {VALUE_STRIP.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="au-strip-item">
              <span className="au-strip-icon" aria-hidden="true"><Icon size={18} /></span>
              <div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="au-section" aria-labelledby="what-we-do-heading">
        <ScrollReveal className="au-section-head">
          <h2 id="what-we-do-heading">Everything HR Needs, Connected in One Platform</h2>
          <p>Brixigo provides an integrated HRMS and payroll platform that brings essential workforce processes together in one place.</p>
        </ScrollReveal>
        <div className="au-feature-grid">
          {FEATURES.map((item, i) => (
            <ScrollReveal key={item.title} delay={i * 40}>
              <FeatureCard {...item} />
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="au-section au-section-alt" aria-labelledby="why-heading">
        <div className="au-split">
          <ScrollReveal className="au-split-copy">
            <h2 id="why-heading">Why Brixigo?</h2>
            <p>
              By bringing these processes together, Brixigo helps businesses reduce manual work, improve accuracy, and make everyday HR operations more efficient.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={80}>
            <WhyDashboard />
          </ScrollReveal>
        </div>
      </section>

      <section className="au-section au-india" aria-labelledby="india-heading">
        <ScrollReveal className="au-section-head au-section-head-light">
          <h2 id="india-heading">Built for Indian Payroll</h2>
          <p>Payroll requirements can be complex, particularly when businesses need to manage statutory deductions, taxes, employee information, and changing compliance requirements.</p>
          <p>Brixigo is designed with Indian businesses in mind, helping HR and finance teams manage payroll processes and statutory requirements through a centralized platform.</p>
          <p>Our platform supports payroll-related processes involving areas such as PF, ESI, TDS, Professional Tax, and Labour Welfare Fund, helping organizations manage their payroll operations more efficiently.</p>
        </ScrollReveal>
        <div className="au-compliance-grid">
          {COMPLIANCE.map((item, i) => (
            <ScrollReveal key={item.abbr} delay={i * 50}>
              <ComplianceCard {...item} />
            </ScrollReveal>
          ))}
        </div>
        <p className="au-disclaimer">
          Statutory applicability and calculations may depend on applicable laws, employee eligibility, location, and organization-specific configuration.
        </p>
      </section>

      <section className="au-section" aria-labelledby="approach-heading">
        <ScrollReveal className="au-section-head">
          <h2 id="approach-heading">Our Approach</h2>
        </ScrollReveal>
        <div className="au-approach-grid">
          {APPROACH.map((item, i) => (
            <ScrollReveal key={item.title} delay={i * 40}>
              <ApproachCard index={i + 1} {...item} />
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="au-section au-section-alt" aria-labelledby="ess-heading">
        <div className="au-split au-split-ess">
          <ScrollReveal>
            <PhoneMockup />
          </ScrollReveal>
          <ScrollReveal className="au-split-copy" delay={80}>
            <h2 id="ess-heading">Empowering HR Teams and Employees</h2>
            <p>
              Brixigo is not only designed for HR and finance teams. Our platform also provides employees with self-service capabilities, allowing them to access information such as payslips, attendance, leave balances, and other relevant HR information.
            </p>
            <p>
              With mobile access, employees and managers can stay connected with important HR activities without depending entirely on manual processes.
            </p>
            <ul className="au-bullet-list">
              {ESS_BULLETS.map((item) => (
                <li key={item}>
                  <CheckCircle2 size={16} aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </ScrollReveal>
        </div>
      </section>

      <section className="au-vision" aria-labelledby="vision-heading">
        <div className="au-vision-bg" aria-hidden="true" />
        <ScrollReveal className="au-vision-inner">
          <p className="au-eyebrow au-eyebrow-light">Our Vision</p>
          <h2 id="vision-heading" className="au-vision-quote">
            Make HR and payroll management simpler, more accessible, and more efficient for organizations.
          </h2>
          <p>
            We aim to build technology that helps businesses spend less time on administrative processes and more time building productive and engaged workplaces.
          </p>
        </ScrollReveal>
      </section>

      <section className="au-section" aria-labelledby="commitment-heading">
        <ScrollReveal className="au-section-head">
          <h2 id="commitment-heading">Our Commitment</h2>
          <p>At Brixigo, we are committed to continuously improving our products and services based on the evolving needs of businesses and HR teams.</p>
          <p>We focus on building reliable technology, improving automation, supporting payroll and HR operations, and providing responsive customer support.</p>
        </ScrollReveal>
        <div className="au-commit-grid">
          {COMMITMENTS.map((item, i) => (
            <ScrollReveal key={item.title} delay={i * 60}>
              <article className="au-commit-card">
                <div className="mk-feature-card-icon" aria-hidden="true">
                  <item.icon size={22} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </article>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="au-section au-section-alt" aria-labelledby="choose-heading">
        <ScrollReveal className="au-section-head">
          <h2 id="choose-heading">A Simpler Way to Manage Your Workforce</h2>
          <p>Businesses choose Brixigo to bring their HR and payroll processes together through a single platform.</p>
          <p>With payroll automation, attendance and leave management, employee self-service, reporting, and compliance-focused features, Brixigo helps organizations manage their workforce with greater efficiency and visibility.</p>
        </ScrollReveal>
        <div className="au-benefit-grid">
          {BENEFITS.map(({ icon: Icon, label }, i) => (
            <ScrollReveal key={label} delay={i * 35} className="au-benefit">
              <span className="au-benefit-icon" aria-hidden="true"><Icon size={18} /></span>
              <span>{label}</span>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="au-section" aria-labelledby="snapshot-heading">
        <ScrollReveal className="au-section-head">
          <h2 id="snapshot-heading">{COMPANY.legalName}</h2>
        </ScrollReveal>
        <div className="au-snapshot-grid">
          <ScrollReveal className="au-info-card">
            <Building2 size={20} />
            <h3>Company</h3>
            <p>{COMPANY.legalName}</p>
          </ScrollReveal>
          <ScrollReveal className="au-info-card" delay={50}>
            <MapPin size={20} />
            <h3>Location</h3>
            <p>{COMPANY.locationShort}</p>
          </ScrollReveal>
          <ScrollReveal className="au-info-card" delay={100}>
            <Briefcase size={20} />
            <h3>Focus</h3>
            <p>{COMPANY.focus}</p>
          </ScrollReveal>
        </div>
        <div className="au-contact-row">
          <ContactCard icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
          <ContactCard icon={Phone} label="Phone" value={COMPANY.phone} href={COMPANY.phoneHref} />
          <ContactCard icon={MapPin} label="Address" value={COMPANY.address} />
        </div>
      </section>

      <CTASection
        heading="Ready to Simplify HR and Payroll?"
        description="Whether you are a growing business, an established organization, or an institution managing a large workforce, Brixigo provides tools to help simplify your HR and payroll operations."
        statement="Explore Brixigo and discover a simpler way to manage HR and payroll."
        primaryLabel="Get Started"
        primaryTo={MARKETING_ROUTES.login}
        secondaryLabel="Contact Us"
        secondaryTo={MARKETING_ROUTES.contact}
      />
    </MarketingLayout>
  );
}
